import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import { pairsStore } from "@/shared/stores/PairsStore";
import { getPairMaxPayoutPercent } from "@/types/index";
import type { PairMessage } from "@cronika/proto/common/trading_pb";
import { useTranslation } from "@cronika/locales";
import { Button, cn } from "@cronika/ui";
import { observer } from "mobx-react-lite";
import { FC, useState } from "react";

const INITIAL_VISIBLE = 4;

const PairRow: FC<{ pair: PairMessage }> = observer(({ pair }) => {
  const icon = useSymbolIcon(pair.type, pair.baseAsset, pair.quoteAsset);
  const payout = getPairMaxPayoutPercent(pair);

  return (
    <button
      className="w-full flex items-center gap-1 p-1.5 rounded-xl bg-default-500/15 text-left"
      onClick={() => pairsStore.replaceActivePair(pair)}
    >
      {icon ? (
        <img src={icon} alt={pair.symbol} className="w-7 h-7 shrink-0 object-cover rounded-full" />
      ) : (
        <span className="w-7 h-7 shrink-0 flex items-center justify-center bg-default-200 rounded-full text-[9px] font-medium">
          {pair.baseAsset.slice(0, 2)}
        </span>
      )}
      <span className="flex-1 text-[10px] font-medium text-white">{pair.symbol}</span>
      <div className="flex flex-col items-end">
        <span className="text-[10px] text-gray-500">Payout</span>
        <span className="text-sm font-semibold text-success">{payout}%</span>
      </div>
      <span className="text-gray-500 text-base">›</span>
    </button>
  );
});

interface PairLockedBlockProps {
  availablePairs: PairMessage[];
  activePairSymbol?: string;
  /** Narrow sidebar rendering (desktop and mobile-landscape) instead of the full-width mobile-portrait drawer. */
  compact?: boolean;
  /** Called with the pointer's clientY when the user starts dragging the header — lets the parent drawer expand on drag. */
  onHeaderDragStart?: (clientY: number) => void;
}

export const PairLockedBlock: FC<PairLockedBlockProps> = observer(
  ({ availablePairs, activePairSymbol, compact, onHeaderDragStart }) => {
    const { t } = useTranslation("trading");
    const [showAll, setShowAll] = useState(false);

    const visiblePairs = showAll ? availablePairs : availablePairs.slice(0, INITIAL_VISIBLE);
    const hasMore = availablePairs.length > INITIAL_VISIBLE;

    return (
      <div className={cn("flex flex-col h-full overflow-y-auto py-4", compact ? "px-1" : "px-5")}>
        {/* Lock icon + title — also the drag handle to expand the drawer */}
        <div
          className={cn(
            "flex items-center gap-4 mb-6",
            compact && "flex-col",
            onHeaderDragStart && "touch-none select-none cursor-grab active:cursor-grabbing",
          )}
          onPointerDown={onHeaderDragStart ? (e) => onHeaderDragStart(e.clientY) : undefined}
        >
          <div className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-full bg-[#F31260]/10">
            <svg width="28" height="32" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22 14V10C22 5.58 18.42 2 14 2C9.58 2 6 5.58 6 10V14H4C2.9 14 2 14.9 2 16V28C2 29.1 2.9 30 4 30H24C25.1 30 26 29.1 26 28V16C26 14.9 25.1 14 24 14H22ZM14 24C12.9 24 12 23.1 12 22C12 20.9 12.9 20 14 20C15.1 20 16 20.9 16 22C16 23.1 15.1 24 14 24ZM19 14H9V10C9 7.24 11.24 5 14 5C16.76 5 19 7.24 19 10V14Z"
                fill="#F31260"
              />
            </svg>
          </div>
          <div className={cn(compact && "text-center")}>
            <p className="text-white font-bold text-[18px]">{t("pair-locked.title")}</p>
            <p className="text-default-500 text-[12px] leading-relaxed mt-1">
              {(() => {
                const [before, after] = t("pair-locked.description-inactive", { pair: "%%PAIR%%" }).split(
                  "%%PAIR%%",
                );
                return (
                  <>
                    {before}
                    <span className="text-white font-semibold">{activePairSymbol}</span>
                    {after}
                  </>
                );
              })()}
            </p>
          </div>
        </div>

        {availablePairs.length > 0 && (
          <div>
            <div className="flex items-start flex-col justify-between mb-3">
              <span className="text-white font-semibold text-sm">{t("pair-locked.available-pairs")}</span>
              <span className="text-gray-500 text-xs">
                {t("pair-locked.pairs-count", { count: availablePairs.length })}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {visiblePairs.map((pair) => (
                <PairRow key={pair.id} pair={pair} />
              ))}
            </div>

            {hasMore && !showAll && (
              <Button
                variant="light"
                fullWidth
                className="mt-3 h-auto min-h-10 whitespace-normal py-2 text-center text-sm leading-tight text-accent-warm border border-accent-warm/15 bg-accent-warm/15"
                onClick={() => setShowAll(true)}
              >
                {t("pair-locked.view-all")}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  },
);
